import { FC, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { trailsService } from '@/services/trails.service';
import { certificateService } from '@/services/certificate.service';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { TrailDetailAPI } from '@/types/trailDetail';

const LEVEL_LABELS: Record<string, string> = {
  BEGINNER: 'Iniciante',
  INTERMEDIATE: 'Intermediário',
  ADVANCED: 'Avançado',
};

const TrailDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trail, setTrail] = useState<TrailDetailAPI | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await trailsService.getTrailDetail(id);
        setTrail(data);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr?.response?.status === 403) {
          setError('Você não tem acesso a este curso. Ele pertence a outro setor.');
        } else {
          setError('Erro ao carregar trilha');
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !trail) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <div className="text-center">
          <p className="text-red-400 text-lg font-bold">Erro ao carregar trilha</p>
          <p className="text-content-muted mt-2">{error}</p>
          <Link to="/trilhas" className="text-accent mt-4 inline-block hover:underline">
            Voltar para Trilhas
          </Link>
        </div>
      </div>
    );
  }

  const lessons = [...trail.lessons].sort((a, b) => a.order - b.order);
  const completedCount = lessons.filter((l) => l.is_completed).length;
  const progressPercent = trail.xp_reward > 0
    ? Math.round((trail.xp_earned / trail.xp_reward) * 100)
    : 0;
  const isCompleted = trail.status === 'completed';
  const hasStarted = trail.last_lesson_id !== null || isCompleted;
  const firstLessonId = lessons[0]?.id;

  const handleStart = () => {
    if (!id) return;
    if (isCompleted) {
      // If completed, go to first lesson for review
      if (firstLessonId) navigate(`/trilhas/${id}/aula/${firstLessonId}`);
      return;
    }
    const targetLesson = trail.last_lesson_id || firstLessonId;
    if (targetLesson) {
      navigate(`/trilhas/${id}/aula/${targetLesson}`);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!id) return;
    try {
      const cert = await certificateService.generate(id);
      navigate(`/certificado/${cert.id}`);
    } catch {
      // If generation fails, try listing existing certificates
      try {
        const certs = await certificateService.list();
        const cert = certs.find((c) => c.trail_id === id);
        if (cert) navigate(`/certificado/${cert.id}`);
      } catch {
        // ignore
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-surface-primary">
      <div className="max-w-4xl mx-auto px-6 py-8 w-full flex-1">
        {/* Back */}
        <Link to="/trilhas" className="flex items-center gap-2 text-content-muted hover:text-content-primary mb-6 text-sm">
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Voltar para Trilhas
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {trail.thumbnail && (
            <img
              src={trail.thumbnail}
              alt={trail.title}
              className="w-full md:w-64 h-40 object-cover rounded-xl"
            />
          )}
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              {trail.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="text-xs px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
              <span className="text-xs px-2.5 py-1 rounded-full bg-surface-tertiary text-content-secondary font-medium">
                {LEVEL_LABELS[trail.level] || trail.level}
              </span>
            </div>
            <h1 className="text-3xl font-bold mb-2">{trail.title}</h1>
            <p className="text-content-secondary mb-4">{trail.description}</p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-content-muted">
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-lg">schedule</span>
                {trail.duration_minutes} min
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-lg">menu_book</span>
                {trail.lesson_count} aulas
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-lg">star</span>
                {trail.xp_reward} XP
              </span>
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-lg">group</span>
                {trail.enrolled_count} alunos
              </span>
            </div>
          </div>
        </div>

        {/* XP Progress */}
        <div className={`bg-surface-card border rounded-xl p-5 mb-8 ${isCompleted ? 'border-green-500/30' : 'border-theme-border'}`}>
          {isCompleted && (
            <div className="flex items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-green-400" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
              <span className="text-sm font-bold text-green-400">Curso Concluído</span>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progresso</span>
            <span className={`text-sm font-bold ${isCompleted ? 'text-green-400' : 'text-accent'}`}>
              {trail.xp_earned} / {trail.xp_reward} XP
            </span>
          </div>
          <div className="h-3 bg-surface-tertiary rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isCompleted ? 'bg-green-500' : 'bg-accent'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-content-muted">
            {completedCount} de {lessons.length} aulas concluídas
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="mb-8 flex flex-wrap gap-3">
          {isCompleted ? (
            <>
              <button
                onClick={handleDownloadCertificate}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-bold text-base transition-colors"
              >
                <span className="material-symbols-outlined">workspace_premium</span>
                Ver Certificado
              </button>
              <button
                onClick={handleStart}
                disabled={!firstLessonId}
                className="flex items-center gap-2 bg-surface-card border border-theme-border text-content-primary hover:bg-surface-hover px-8 py-3 rounded-xl font-bold text-base transition-colors disabled:opacity-40"
              >
                <span className="material-symbols-outlined">replay</span>
                Revisar Conteúdo
              </button>
            </>
          ) : (
            <button
              onClick={handleStart}
              disabled={!firstLessonId}
              className="bg-accent hover:bg-accent-hover text-accent-contrast px-8 py-3 rounded-xl font-bold text-base transition-colors disabled:opacity-40"
            >
              {hasStarted ? 'Continuar de onde parou' : 'Começar Trilha'}
            </button>
          )}
        </div>

        {/* Instructor */}
        {trail.instructor_name && (
          <div className="flex items-center gap-4 mb-8 bg-surface-card border border-theme-border rounded-xl p-4">
            {trail.instructor_avatar && (
              <img
                src={trail.instructor_avatar}
                alt={trail.instructor_name}
                className="w-12 h-12 rounded-full object-cover"
              />
            )}
            <div>
              <p className="font-medium">{trail.instructor_name}</p>
              {trail.instructor_title && (
                <p className="text-sm text-content-muted">{trail.instructor_title}</p>
              )}
            </div>
          </div>
        )}

        {/* Outcomes */}
        {trail.outcomes.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-3">O que você vai aprender</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {trail.outcomes.map((outcome, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <span className="material-symbols-outlined text-green-500 text-lg shrink-0">check_circle</span>
                  <span className="text-content-secondary">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lesson List */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">Conteúdo do Curso</h2>
          <div className="space-y-2">
            {lessons.map((lesson, idx) => {
              const isCurrent = !isCompleted && lesson.id === trail.last_lesson_id;

              return (
                <div
                  key={lesson.id}
                  onClick={() => id && navigate(`/trilhas/${id}/aula/${lesson.id}`)}
                  className={`flex items-center gap-4 bg-surface-card border rounded-xl px-4 py-3 transition-all cursor-pointer ${
                    isCurrent
                      ? 'border-accent/30 bg-accent/5'
                      : lesson.is_completed
                      ? 'border-theme-border hover:border-accent/20'
                      : 'border-theme-border hover:border-accent/20'
                  }`}
                >
                  <span className="text-content-muted text-sm font-bold w-8 text-center">{idx + 1}</span>
                  {lesson.is_completed ? (
                    <span className="material-symbols-outlined text-green-400" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  ) : isCurrent ? (
                    <span className="material-symbols-outlined text-accent">play_circle</span>
                  ) : (
                    <span className="material-symbols-outlined text-content-muted/60">radio_button_unchecked</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isCurrent ? 'text-accent' : ''}`}>{lesson.title}</p>
                    <p className="text-xs text-content-muted">{lesson.duration_minutes} min</p>
                  </div>
                  {lesson.is_completed ? (
                    <span className="text-xs text-green-400 font-medium">{lesson.xp_reward} XP</span>
                  ) : (
                    <span className="text-xs text-content-muted">{lesson.xp_reward} XP</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Requirements */}
        {trail.requirements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-3">Pré-requisitos</h2>
            <ul className="space-y-1">
              {trail.requirements.map((req, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-content-secondary">
                  <span className="material-symbols-outlined text-content-muted text-lg">info</span>
                  {req}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

    </div>
  );
};

export default TrailDetailPage;
